import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { 
  FAQOrGlossaryItem,
  getFAQItems,
  getGlossaryItems,
  searchItems,
  UserLevel,
  Relevance
} from '../../data/faqAndGlossaryData';
import ReactMarkdown from 'react-markdown';

// Define a tabbed interface for FAQ and Glossary
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`faq-tabpanel-${index}`}
      aria-labelledby={`faq-tab-${index}`}
      style={{ width: '100%' }}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `faq-tab-${index}`,
    'aria-controls': `faq-tabpanel-${index}`,
  };
}

// Define user level filter options
const userLevelFilters: { label: string; value: UserLevel | 'all' }[] = [
  { label: 'All Levels', value: 'all' },
  { label: 'Beginner', value: 'beginner' },
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Advanced', value: 'advanced' },
];

// Define relevance filter options
const relevanceFilters: { label: string; value: Relevance | 'all' }[] = [
  { label: 'All Relevance', value: 'all' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const FAQPage: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [userLevel, setUserLevel] = useState<UserLevel | 'all'>('all');
  const [relevance, setRelevance] = useState<Relevance | 'all'>('all');
  
  // Get the FAQ and Glossary items
  const faqItems = useMemo(() => getFAQItems(), []);
  const glossaryItems = useMemo(() => getGlossaryItems(), []);
  
  // Filter items based on search query, user level, and relevance
  const filteredFAQItems = useMemo(() => {
    let items = faqItems;
    
    // Apply search filter
    if (searchQuery) {
      items = searchItems(items, searchQuery) as typeof items;
    }
    
    // Apply user level filter
    if (userLevel !== 'all') {
      items = items.filter(item => item.userLevel === userLevel || item.userLevel === 'all');
    }
    
    // Apply relevance filter
    if (relevance !== 'all') {
      items = items.filter(item => item.relevance === relevance);
    }
    
    return items;
  }, [faqItems, searchQuery, userLevel, relevance]);
  
  const filteredGlossaryItems = useMemo(() => {
    let items = glossaryItems;
    
    // Apply search filter
    if (searchQuery) {
      items = searchItems(items, searchQuery) as typeof items;
    }
    
    // Apply user level filter
    if (userLevel !== 'all') {
      items = items.filter(item => item.userLevel === userLevel || item.userLevel === 'all');
    }
    
    // Apply relevance filter
    if (relevance !== 'all') {
      items = items.filter(item => item.relevance === relevance);
    }
    
    // Sort glossary items alphabetically by term
    return [...items].sort((a, b) => a.term.localeCompare(b.term));
  }, [glossaryItems, searchQuery, userLevel, relevance]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Reset filters when changing tabs
  useEffect(() => {
    setSearchQuery('');
    setUserLevel('all');
    setRelevance('all');
  }, [tabValue]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
        SMA Help Center
      </Typography>
      
      {/* Tabs for FAQ and Glossary */}
      <Paper elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="FAQ and Glossary tabs"
          variant="fullWidth"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontSize: '1.1rem',
              fontWeight: 'medium',
              py: 2,
            }
          }}
        >
          <Tab label="Frequently Asked Questions" {...a11yProps(0)} />
          <Tab label="Glossary" {...a11yProps(1)} />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {/* Search and filter section */}
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={tabValue === 0 ? "Search questions..." : "Search glossary terms..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              <Typography variant="body2" sx={{ mr: 1, alignSelf: 'center' }}>
                Filters:
              </Typography>
              
              {/* User level filters */}
              {userLevelFilters.map((filter) => (
                <Chip
                  key={filter.value}
                  label={filter.label}
                  onClick={() => setUserLevel(filter.value)}
                  color={userLevel === filter.value ? 'primary' : 'default'}
                  variant={userLevel === filter.value ? 'filled' : 'outlined'}
                  sx={{ m: 0.5 }}
                />
              ))}
              
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              
              {/* Relevance filters */}
              {relevanceFilters.map((filter) => (
                <Chip
                  key={filter.value}
                  label={filter.label}
                  onClick={() => setRelevance(filter.value)}
                  color={relevance === filter.value ? 'secondary' : 'default'}
                  variant={relevance === filter.value ? 'filled' : 'outlined'}
                  sx={{ m: 0.5 }}
                />
              ))}
            </Box>
          </Box>
          
          {/* Tab content */}
          <TabPanel value={tabValue} index={0}>
            {filteredFAQItems.length === 0 ? (
              <Typography variant="body1" align="center" sx={{ py: 4 }}>
                No matching questions found. Please try a different search or filter.
              </Typography>
            ) : (
              <div>
                {filteredFAQItems.map((item, index) => (
                  <Accordion 
                    key={index} 
                    disableGutters
                    elevation={0}
                    sx={{ 
                      mb: 2, 
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '8px !important',
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`faq-content-${index}`}
                      id={`faq-header-${index}`}
                      sx={{ 
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.03)',
                        fontWeight: 'bold',
                      }}
                    >
                      <Typography variant="subtitle1">{item.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 2, pb: 3 }}>
                      <ReactMarkdown>{item.answer}</ReactMarkdown>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>
            )}
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            {filteredGlossaryItems.length === 0 ? (
              <Typography variant="body1" align="center" sx={{ py: 4 }}>
                No matching glossary terms found. Please try a different search or filter.
              </Typography>
            ) : (
              <div>
                {filteredGlossaryItems.map((item, index) => (
                  <Accordion 
                    key={index}
                    disableGutters
                    elevation={0}
                    sx={{ 
                      mb: 2, 
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '8px !important',
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`glossary-content-${index}`}
                      id={`glossary-header-${index}`}
                      sx={{ 
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.03)',
                        fontWeight: 'bold',
                      }}
                    >
                      <Typography variant="subtitle1">{item.term}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 2, pb: 3 }}>
                      <ReactMarkdown>{item.definition}</ReactMarkdown>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>
            )}
          </TabPanel>
        </Box>
      </Paper>
      
      {/* Footer info */}
      <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 4 }}>
        If you have additional questions, please contact SMA support.
      </Typography>
    </Container>
  );
};

export default FAQPage;
